import { useContext, useEffect, useMemo, useState } from "react";
import {
  AutoTradingGroupsData,
  AutoTradingType,
  ClientData,
  ClientStatus,
  ConfigurationItem,
  DisplayResultMessage,
  ExchangeAccountType,
  GetWhitelistIpsRequest,
  PrecisionConfig,
  WorkspaceType,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { EXCHANGES, PRIMARY_EXCHANGES } from "@shared/common/constants";
import {
  getChildren,
  getIsMicaAccount,
  getItemKey,
  getName,
} from "@shared/features/configuration/utils";
import { capitalizeFirstLetter } from "@shared/common/utils";
import { CUSTOM_STRATEGIES_ITEM_NAME } from "@shared/features/configuration/constants";
import { PriceRange } from "@shared/features/tradingTerminal/common/types";
import { mapValues } from "lodash";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import { getMinAndMaxPrices } from "@shared/features/tradingTerminal/common/utils";
import { selectWorkspace } from "@shared/slices/common.slice";
import { INACTIVE_ACCOUNT_STATUSES } from "@shared/features/portfolio/constants";
import { useExchangesMetadata } from "@shared/utils/hooks/misc";

export const useAccountStatusTag = (
  status: ClientStatus,
  inDrawer?: boolean
) => {
  const theme = useTheme();
  let tagText = "Active";
  let tagColor = theme.colors.green;
  let popoverContent: string[] | undefined;
  if (status === ClientStatus.INVALID) {
    tagText = "Invalid";
    tagColor = theme.colors.red;
    popoverContent = inDrawer
      ? [
          "Your account's API keys are no longer valid.",
          "Please ensure your API keys are still active on the exchange and use the 'Re-validate API keys' button.",
          "Or create new API keys, click the edit button above and update the account's API keys.",
        ]
      : [
          "Your account's API keys are no longer valid.",
          "To re-activate your account please click the account card, click the edit button at the top and update the account's API keys.",
        ];
  } else if (INACTIVE_ACCOUNT_STATUSES.has(status)) {
    tagText = "Inactive";
    tagColor = theme.colors.darkYellow;
  }

  return {
    tagText,
    title: tagText + " Account",
    tagColor,
    popoverContent,
  };
};

export const useIPWhitelistTag = (inDrawer?: boolean) => {
  const appTheme = useTheme();
  const articleId = inDrawer ? "6870965" : "6871341";

  const baseText = inDrawer
    ? "Please use the edit button above, copy your list of IPs and paste them into Binance's list of trusted IPs. This is to enhance the safety of your API keys as well as ensure they will not expire after a short duration."
    : "This is recommended to enhance the safety of your API keys as well as ensure they will not expire after a short duration.";
  let popoverTexts = [
    "Your account's API keys are not IP-restricted (whitelisted).",
    baseText,
  ];

  return {
    popoverTexts,
    articleId,
    tagColor: appTheme.colors.primaryBlue,
    tagText: "Not IP Whitelisted",
  };
};

interface UseWhitelistIPsProps {
  args?: GetWhitelistIpsRequest;
}

export const useWhitelistIPs = ({ args }: UseWhitelistIPsProps) => {
  const { data: whitelistIPs } =
    sharedLib.sharedApi.configuration.useGetWhitelistIPsQuery(args);

  return {
    mainMessage:
      "You must also add Cornix's IP addresses from below to the Binance list of trusted IPs. This is to enhance the safety of your API keys as well as ensure they will not expire after a short duration.",
    copyText: whitelistIPs?.join(" "),
    notificationPrefix: "IP addresses were",
  };
};

export const useWhitelistIPsComponent = (exchange?: string) => {
  const [copyIPsClicked, setCopyIPsClicked] = useState(false);
  const [showCopyIPsWarning, setShowCopyIPsWarning] = useState(false);
  const isBinanceExchange = exchange === PRIMARY_EXCHANGES.BINANCE;

  useEffect(() => {
    setShowCopyIPsWarning(false);
    setCopyIPsClicked(false);
  }, [exchange]);

  const checkAllowSave = () => {
    if (isBinanceExchange && !copyIPsClicked && !showCopyIPsWarning) {
      setShowCopyIPsWarning(true);
      return false;
    }
    return true;
  };

  return {
    show: isBinanceExchange,
    checkAllowSave,
    copyIPsClicked,
    setCopyIPsClicked,
    showCopyIPsWarning,
    setShowCopyIPsWarning,
  };
};

export const useMemberWhitelistIps = () => {
  const [getMemberWhitelistIps, getMemberWhitelistIpsQuery] =
    sharedLib.sharedApi.configuration.useLazyGetMemberWhitelistIpsQuery();

  return {
    getMemberWhitelistIps,
    getMemberWhitelistIpsQuery,
    copyText: getMemberWhitelistIpsQuery.data?.join(" "),
    noWhitelistIpsErrorMessage:
      "This user doesn't have any assigned whitelist IPs",
    noCornixAccountErrorMessage: "This user doesn't have a Cornix account",
  };
};

export const useChangeAccountStatus = (
  onSuccess: (() => void) | undefined,
  displayResultMessage: DisplayResultMessage
) => {
  const [changeClientStatus, { isLoading: isLoadingChangeStatus }] =
    sharedLib.sharedApi.configuration.useChangeClientStatusMutation();
  const [refreshClientKeys, { isLoading: isLoadingRefreshKeys }] =
    sharedLib.sharedApi.configuration.useRefreshClientKeysMutation();

  const handleChangeClientStatus = (
    clientId?: number,
    accountStatus?: ClientStatus,
    isRefreshKeys?: boolean
  ) => {
    if (!accountStatus || !clientId) return;
    const isDelete = accountStatus === ClientStatus.DELETED;
    const isDeactivate = accountStatus === ClientStatus.DEACTIVATED_BY_USER;
    const isActivate = accountStatus === ClientStatus.VALID;
    let actionName = "";
    if (isDelete) actionName = "delete";
    else if (isDeactivate) actionName = "deactivate";
    else if (isActivate) {
      actionName = isRefreshKeys ? "validate" : "activate";
    }
    const successMessage = `Account was ${actionName}d successfully.`;
    const errorMessage = `Failed to ${actionName} account.`;
    let promise;
    if (isActivate) {
      promise = displayResultMessage(
        refreshClientKeys({
          client_id: clientId,
        }).unwrap(),
        successMessage,
        errorMessage
      );
    } else {
      promise = displayResultMessage(
        changeClientStatus({
          client_id: clientId,
          status: accountStatus,
        }).unwrap(),
        successMessage,
        errorMessage
      );
    }
    return promise.then(() => onSuccess?.());
  };

  return {
    isLoading: isLoadingChangeStatus || isLoadingRefreshKeys,
    handleChangeClientStatus,
  };
};

export const useDeactivateAccountModal = (
  accountStatus: ClientStatus | undefined,
  onSuccess: () => void,
  clientId: number | undefined,
  displayResultMessage: DisplayResultMessage
) => {
  const { isLoading, handleChangeClientStatus } = useChangeAccountStatus(
    onSuccess,
    displayResultMessage
  );
  const workspace = useSelector(selectWorkspace);
  const isDemo = workspace === WorkspaceType.DEMO;

  const isDelete = accountStatus === ClientStatus.DELETED;
  const actionName = isDelete ? "delete" : "deactivate";
  const titleActionName = capitalizeFirstLetter(actionName);
  const texts = [
    `All the Cornix Open Trades and Active Bots related to this account will ${
      isDelete ? "be deleted" : "remain open but will no longer be managed"
    }. `,
    `The trades’ pending orders on the exchange will remain intact - if you wish to cancel them - close this account’s `,
    ` before ${isDelete ? "deleting" : "deactivating"}.`,
    `Are you sure you wish to ${actionName} this account?`,
  ];
  if (isDelete) {
    texts.splice(
      3,
      0,
      "You will no longer be able to trade on this account and will lose all its related data. This action is irreversible."
    );
  }

  return {
    handleChangeClientStatus: () =>
      handleChangeClientStatus(clientId, accountStatus),
    isLoading,
    title: `${titleActionName} Account`,
    texts,
    okText: titleActionName,
    isDemo,
  };
};

export const useSharedCustomStrategies = (
  itemValue: any,
  itemChildren: ConfigurationItem[]
) => {
  const customStrategiesItem = itemChildren.find(
    (child) => getName(child) === CUSTOM_STRATEGIES_ITEM_NAME
  );
  const customStrategies = getChildren(customStrategiesItem);
  const selectedCustomStrategy = customStrategies.find(
    (strategy: ConfigurationItem) =>
      getItemKey(strategy, "value").type === itemValue
  );

  return {
    customStrategiesItem,
    customStrategies,
    selectedCustomStrategy,
  };
};

export const useSharedPriceRangeItem = (
  itemValue: PriceRange,
  originalItemValue: PriceRange,
  precisionConfig: PrecisionConfig,
  exchange?: string
) => {
  const { getExchangePriceFactorLimits } = useContext(
    ExchangesMetadataContext
  )!;
  const priceFactorLimits = getExchangePriceFactorLimits(exchange);
  const priceLimits = getMinAndMaxPrices(
    originalItemValue.min,
    precisionConfig?.price || 0,
    priceFactorLimits
  );
  const validationMessage = `Prices should be between ${priceLimits.min} and ${priceLimits.max}`;
  const error =
    itemValue.min < priceLimits.min ||
    itemValue.min > priceLimits.max ||
    itemValue.max < priceLimits.min ||
    itemValue.max > priceLimits.max;

  return {
    validationMessage,
    error,
  };
};

export const useAutoTradingGroups = () => {
  const [getAutoTradingGroupsByType, queryStatus] =
    sharedLib.sharedApi.configuration.useLazyGetAutoTradingGroupsByTypeQuery();
  const data = queryStatus.data;

  const getParsedSetsData = (data?: AutoTradingGroupsData) => {
    const parsedData: ParsedAutoTradingSetsData = mapValues(
      data,
      (value) => new Set(value)
    );
    parsedData["all"] = new Set(
      data?.[AutoTradingType.LEGACY]?.concat(data?.[AutoTradingType.BOT])
    );
    return parsedData;
  };

  const autoTradingGroupsSetData = useMemo(
    () => getParsedSetsData(data),
    [data]
  );

  const getIsGroupAlreadyActiveFunction =
    (autoTradingGroupsSetData: ParsedAutoTradingSetsData) =>
    (groupId?: number, autoTradingType?: AutoTradingType) => {
      if (!groupId) return;
      if (autoTradingType)
        return autoTradingGroupsSetData[autoTradingType]?.has(groupId);
      return autoTradingGroupsSetData?.all?.has(groupId);
    };

  const isGroupAlreadyActive = getIsGroupAlreadyActiveFunction(
    autoTradingGroupsSetData
  );

  const getAutoTradingData = (
    preferCacheValue?: boolean
  ): Promise<{ isGroupAlreadyActive: IsGroupAlreadyActive }> => {
    return getAutoTradingGroupsByType(undefined, preferCacheValue)
      .unwrap()
      .then((result) => {
        const parsedSetsData = getParsedSetsData(result);
        return {
          isGroupAlreadyActive: getIsGroupAlreadyActiveFunction(parsedSetsData),
        };
      });
  };

  return {
    getAutoTradingData,
    isGroupAlreadyActive,
    isFetching: queryStatus.isFetching,
  };
};

type IsGroupAlreadyActive = (
  groupId?: number,
  autoTradingType?: AutoTradingType
) => boolean | undefined;

interface ParsedAutoTradingSetsData {
  [AutoTradingType.BOT]: Set<number>;
  [AutoTradingType.LEGACY]: Set<number>;
  all?: Set<number>;
}

export const useAccountAlternativeUsdPairs = (account?: ClientData) => {
  const { getExchangeMetadata } = useExchangesMetadata();
  const exchangeMetadata = getExchangeMetadata(account?.exchange);

  const getAccountData = (account?: ClientData) => {
    const isSinglePairWarning = exchangeMetadata?.pairs?.length === 1;
    const singlePair = isSinglePairWarning
      ? exchangeMetadata?.pairs?.[0]
      : undefined;
    const isMicaCreditMode =
      account?.exchange_account_type === ExchangeAccountType.MICA_CREDIT_MODE;
    const isBitmex = account?.exchange === EXCHANGES.BITMEX;
    const isInverse = exchangeMetadata?.is_inverse_exchange;
    const isDisabled =
      isSinglePairWarning || isMicaCreditMode || isBitmex || isInverse;
    const isDefaultEnabled =
      !isDisabled &&
      (getIsMicaAccount(account) ||
        account?.exchange === EXCHANGES.COINBASE_SPOT ||
        account?.exchange === EXCHANGES.COINBASE_FUTURES);

    return {
      isSinglePairWarning,
      isMicaCreditMode,
      isBitmex,
      isInverse,
      isDisabled,
      isDefaultEnabled,
      singlePair,
    };
  };

  return {
    ...getAccountData(account),
    getAccountData,
  };
};
